import { useEffect, useState } from 'react';
import type { Puzzle } from '@familyhub/shared';

/**
 * The beginner puzzle pack (web/scripts/build-chess-puzzles.mjs, from the CC0
 * Lichess database). Loaded as its own chunk on first use rather than in the
 * main bundle — it is 400 KB nobody else needs — and precached with the rest of
 * the build, so it still opens with no signal.
 */

export interface Pack {
  puzzles: Puzzle[];
  byId: Map<string, Puzzle>;
  /** The fixed Woodpecker set, in solving order. */
  woodpecker: Puzzle[];
}

/** Bump when the Woodpecker list in the pack changes — a cycle must never change set under it. */
export const WOODPECKER_SET_NO = 1;

type Raw = { themes: string[]; puzzles: [string, string, string, number, number][]; woodpecker: string[] };

let loaded: Promise<Pack> | null = null;

export function loadPack(): Promise<Pack> {
  loaded ??= import('./data/puzzles.json').then((m) => {
    const raw = (m.default ?? m) as unknown as Raw;
    const puzzles = raw.puzzles.map(([id, fen, moves, rating, t]) => ({
      id,
      fen,
      moves: moves.split(' '),
      rating,
      theme: raw.themes[t]!,
    }));
    const byId = new Map(puzzles.map((p) => [p.id, p]));
    return { puzzles, byId, woodpecker: raw.woodpecker.map((id) => byId.get(id)!).filter(Boolean) };
  });
  return loaded;
}

export function usePack(): Pack | null {
  const [pack, setPack] = useState<Pack | null>(null);
  useEffect(() => {
    let live = true;
    void loadPack().then((p) => live && setPack(p));
    return () => {
      live = false;
    };
  }, []);
  return pack;
}
